import { useState, useEffect, useMemo } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  MagicWand,
  ArrowsClockwise,
  CheckCircle,
  WarningCircle,
  CalendarBlank,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import DateInput from "./DateInput";
/**
 * Bulk Generate POs — one click to issue draft purchase orders to every in-house
 * operative who worked in the given period. Idempotent: re-running won't create
 * duplicates (existing drafts are reported in the "already drafted" bucket).
 */

// Helper: format a Date as YYYY-MM-DD in local time
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Helper: get Monday of the week containing the given date (UK convention)
function mondayOf(d) {
  const out = new Date(d);
  const dow = out.getDay(); // 0 = Sunday, 1 = Monday, ... 6 = Saturday
  const diff = dow === 0 ? -6 : 1 - dow;
  out.setDate(out.getDate() + diff);
  return out;
}

function thisWeek() {
  const mon = mondayOf(new Date());
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return { period_start: ymd(mon), period_end: ymd(sun) };
}

function lastWeek() {
  const mon = mondayOf(new Date());
  mon.setDate(mon.getDate() - 7);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return { period_start: ymd(mon), period_end: ymd(sun) };
}

export default function BulkGenerateInHousePosDialog({ open, onClose, onCreated }) {
  const defaults = useMemo(() => thisWeek(), []);
  const [periodStart, setPeriodStart] = useState(defaults.period_start);
  const [periodEnd, setPeriodEnd] = useState(defaults.period_end);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  // Reset when dialog is opened
  useEffect(() => {
    if (open) {
      const tw = thisWeek();
      setPeriodStart(tw.period_start);
      setPeriodEnd(tw.period_end);
      setRunning(false);
      setResult(null);
    }
  }, [open]);

  const setThisWeek = () => {
    const tw = thisWeek();
    setPeriodStart(tw.period_start);
    setPeriodEnd(tw.period_end);
  };
  const setLastWeek = () => {
    const lw = lastWeek();
    setPeriodStart(lw.period_start);
    setPeriodEnd(lw.period_end);
  };

  const generate = async () => {
    if (!periodStart || !periodEnd) {
      toast.error("Please pick a period start and end date.");
      return;
    }
    if (periodEnd < periodStart) {
      toast.error("Period end must be on or after period start.");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const { data } = await API.post("/in-house/pos/bulk-generate", {
        period_start: periodStart,
        period_end: periodEnd,
      });
      setResult(data);
      const n = (data.created || []).length;
      if (n > 0) {
        toast.success(`Created ${n} draft PO${n === 1 ? "" : "s"} for ${periodStart} → ${periodEnd}`);
        onCreated && onCreated(data);
      } else if ((data.skipped_existing || []).length > 0) {
        toast.info("All operatives already have a draft for this period — nothing new to create.");
      } else {
        toast.info("No one worked in that period.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk generate failed.");
    } finally {
      setRunning(false);
    }
  };

  const created = result?.created || [];
  const skippedNoWork = result?.skipped_no_work || [];
  const skippedExisting = result?.skipped_existing || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="bulk-generate-pos-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-[Chivo]">
            <MagicWand className="h-5 w-5 text-amber-600" weight="duotone" />
            Bulk Generate POs
          </DialogTitle>
          <DialogDescription>
            Auto-create draft purchase orders for every in-house operative who worked in the selected period.
            Lines are built from their scheduled jobs: the day rate from each operative&apos;s saved rate profile goes on the first job of each day only, every job with receipts gets an expenses line, and each day gets a mileage line from the mileage log.
            Re-running for the same period is safe — operatives with an existing draft are reported, not duplicated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Quick shortcuts */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={setThisWeek}
              data-testid="bulk-pos-this-week-btn"
            >
              <CalendarBlank size={14} className="mr-1" /> This week (Mon–Sun)
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={setLastWeek}
              data-testid="bulk-pos-last-week-btn"
            >
              <CalendarBlank size={14} className="mr-1" /> Last week
            </Button>
          </div>

          {/* Period inputs */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="bulk-pos-period-start" className="text-xs">Period start</Label>
              <DateInput
                id="bulk-pos-period-start"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                data-testid="bulk-pos-period-start"
              />
            </div>
            <div>
              <Label htmlFor="bulk-pos-period-end" className="text-xs">Period end</Label>
              <DateInput
                id="bulk-pos-period-end"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                data-testid="bulk-pos-period-end"
              />
            </div>
          </div>

          {/* Result summary */}
          {result && (
            <div className="space-y-3" data-testid="bulk-pos-result">
              {created.length > 0 && (
                <div className="border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/30 rounded-md p-3">
                  <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                    <CheckCircle size={16} weight="duotone" />
                    {created.length} draft PO{created.length === 1 ? "" : "s"} created
                  </div>
                  <ul className="text-xs space-y-1 text-emerald-900 dark:text-emerald-200">
                    {created.map((c) => (
                      <li
                        key={c.po_id}
                        className="flex items-center justify-between gap-3"
                        data-testid={`bulk-pos-created-${c.po_number}`}
                      >
                        <span>
                          <span className="font-mono">{c.po_number}</span>
                          {" · "}
                          {c.operative_name}
                          {" · "}
                          {c.lines ?? c.days} line{(c.lines ?? c.days) === 1 ? "" : "s"}{c.jobs != null && <span className="text-muted-foreground"> · {c.jobs} job{c.jobs === 1 ? "" : "s"}{c.expense_lines ? ` · ${c.expense_lines} expenses` : ""}{c.mileage_lines ? ` · ${c.mileage_lines} mileage` : ""}</span>}
                        </span>
                        <span className="font-semibold">
                          £{Number(c.total || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {skippedExisting.length > 0 && (
                <div className="border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/30 rounded-md p-3">
                  <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
                    <WarningCircle size={16} weight="duotone" />
                    {skippedExisting.length} already has a draft (not duplicated)
                  </div>
                  <ul className="text-xs space-y-1 text-amber-900 dark:text-amber-200">
                    {skippedExisting.map((s) => (
                      <li key={s.po_id} data-testid={`bulk-pos-existing-${s.po_number}`}>
                        <span className="font-mono">{s.po_number}</span>
                        {" · "}
                        {s.operative_name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {skippedNoWork.length > 0 && (
                <div className="border border-slate-200 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-900/40 rounded-md p-3">
                  <div className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    {skippedNoWork.length} operative{skippedNoWork.length === 1 ? "" : "s"} had no scheduled work
                  </div>
                  <ul className="text-xs space-y-1 text-slate-600 dark:text-slate-400">
                    {skippedNoWork.map((s) => (
                      <li key={s.operative_id}>{s.operative_name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {created.length === 0 && skippedExisting.length === 0 && skippedNoWork.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-4">
                  No in-house operatives found in this tenant.
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="bulk-pos-close-btn"
          >
            {result ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={generate}
            disabled={running || !periodStart || !periodEnd}
            data-testid="bulk-pos-generate-btn"
          >
            {running ? (
              <>
                <ArrowsClockwise size={14} className="mr-1 animate-spin" />
                Generating…
              </>
            ) : result ? (
              <>
                <MagicWand size={14} className="mr-1" />
                Re-run for this period
              </>
            ) : (
              <>
                <MagicWand size={14} className="mr-1" />
                Generate POs
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
